import { Box } from '@mui/material'
import { useState, type FormEvent } from 'react'
import { useIntl } from 'react-intl'
import type { SettingsFlow } from '@ory/client'

import { tokens } from '@configuration/theme'
import Button from '@base/Button/Button.component'
import Card from '@base/Card/Card.component'
import TextField from '@base/TextField/TextField.component'
import LockIcon from '@icons/Lock.icon'
import { findFieldError, findInput, nodesForGroup } from '@utils/kratos-nodes.utils'

import { writeSubmittedContext } from '../../SettingsPage.utils'

interface Props {
  flow: SettingsFlow
  csrfValue: string
}

const PasswordPanel = ({ flow, csrfValue }: Props) => {
  const intl = useIntl()
  const nodes = nodesForGroup(flow.ui.nodes, 'password')

  const passwordAttrs = findInput(nodes, 'password')
  const passwordError = findFieldError(nodes, 'password', intl)
  const [confirmError, setConfirmError] = useState<string | null>(null)

  if (!passwordAttrs) {
    // Password method disabled for this identity (e.g. OIDC-only). Render
    // a soft notice rather than a hard error — the panel stays a no-op tab.
    return (
      <Card>
        <Box sx={{ color: tokens.businessBlack60 }}>
          {intl.formatMessage({ id: 'settings.password.unavailable' })}
        </Box>
      </Card>
    )
  }

  // Compare on submit so the user sees the mismatch error before the Kratos
  // round-trip. The `password_confirm` field IS included in the native POST
  // (it's a live input), but Kratos's password method binds only known body
  // keys and ignores the extra one — so it's harmless, just not stripped.
  //
  // Lookup by `id` scoped to the submitted form: `namedItem` on
  // `form.elements` returns RadioNodeList when two inputs share a name, and
  // a future name rename would silently break the cast. ids are stable
  // accessibility hooks already used by the <label htmlFor>s, so reusing
  // them keeps the form coupling explicit.
  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    const form = e.currentTarget
    const password = form.querySelector<HTMLInputElement>('#settings-password')?.value ?? ''
    const confirm = form.querySelector<HTMLInputElement>('#settings-password-confirm')?.value ?? ''
    if (password !== confirm) {
      e.preventDefault()
      setConfirmError(intl.formatMessage({ id: 'settings.error.passwordMismatch' }))
      return
    }
    setConfirmError(null)
    writeSubmittedContext({ flowId: flow.id, tab: 'password' })
  }

  const clearConfirmError = (): void => {
    if (confirmError) {
      setConfirmError(null)
    }
  }

  return (
    <Box
      component='form'
      data-testid='password-panel'
      action={flow.ui.action}
      method={flow.ui.method}
      onSubmit={handleSubmit}
      sx={{ width: '100%', minHeight: '360px' }}
    >
      <input type='hidden' name='csrf_token' value={csrfValue} />
      {/* Hidden `method` keeps the POST tagged as password regardless of
       * which submit element fires the form (button click vs. form.submit()).
       * Profile panel relies on this for its confirm-dialog path. */}
      <input type='hidden' name='method' value='password' />

      <Card
        title={intl.formatMessage({ id: 'settings.password.section.title' })}
        description={intl.formatMessage({ id: 'settings.password.section.description' })}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr' },
            gap: '18px',
          }}
        >
          <TextField
            id='settings-password'
            name='password'
            type='password'
            icon={<LockIcon />}
            label={intl.formatMessage({ id: 'settings.field.password.placeholder' })}
            placeholder={intl.formatMessage({ id: 'settings.field.password.placeholder' })}
            autoComplete='new-password'
            required={true}
            error={passwordError}
            onInput={clearConfirmError}
          />
          <TextField
            id='settings-password-confirm'
            name='password_confirm'
            type='password'
            icon={<LockIcon />}
            label={intl.formatMessage({ id: 'settings.field.passwordConfirm.placeholder' })}
            placeholder={intl.formatMessage({ id: 'settings.field.passwordConfirm.placeholder' })}
            autoComplete='new-password'
            required={true}
            error={confirmError}
            onInput={clearConfirmError}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1.5,
            mt: 3,
            pt: '20px',
            borderTop: `1px solid ${tokens.smokeySilver}`,
          }}
        >
          <Button variant='primary' size='md' type='submit'>
            {intl.formatMessage({ id: 'settings.submit.save' })}
          </Button>
        </Box>
      </Card>
    </Box>
  )
}

export default PasswordPanel
